import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Plus, Edit, Trash2, Percent, DollarSign, Tag, Calendar, Loader2, AlertCircle,
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

const auth = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const blank = {
  name: "",
  discount_type: "percentage",
  value: 0,
  applies_to: "all",
  plan_ids: [],
  expires_at: "",
  is_active: true,
  marketing_text: "",
  badge_label: "",
  apply_to_existing: false,
  duration: "forever",
  duration_months: 12,
  billing_cycles: ["monthly", "annual"],
  annual_duration: "forever",
  annual_periods: 1,
  max_redemptions: "",
};

const cycleKey = (cycles) => {
  const c = Array.isArray(cycles) && cycles.length ? cycles : ["monthly", "annual"];
  if (c.includes("monthly") && c.includes("annual")) return "both";
  return c.includes("annual") ? "annual" : "monthly";
};
const cyclesFromKey = (key) =>
  key === "both" ? ["monthly", "annual"] : [key];

const AdminDiscounts = () => {
  const [discounts, setDiscounts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...blank });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [dRes, pRes] = await Promise.all([
        axios.get(`${API}/admin/discounts`, auth()),
        axios.get(`${API}/admin/plans`, auth()),
      ]);
      setDiscounts(dRes.data.discounts || []);
      setPlans(pRes.data.plans || []);
    } catch { toast.error("Failed to load discounts"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ ...blank }); setDialogOpen(true); };
  const openEdit = (d) => {
    setEditing(d.discount_id);
    setForm({
      name: d.name || "",
      discount_type: d.discount_type || "percentage",
      value: d.value || 0,
      applies_to: d.applies_to || "all",
      plan_ids: d.plan_ids || [],
      expires_at: d.expires_at ? d.expires_at.slice(0, 16) : "",
      is_active: d.is_active !== false,
      marketing_text: d.marketing_text || "",
      badge_label: d.badge_label || "",
      apply_to_existing: false,
      duration: ["once", "repeating", "forever"].includes(d.duration) ? d.duration : (d.expires_at ? "once" : "forever"),
      duration_months: d.duration_months || 12,
      billing_cycles: Array.isArray(d.billing_cycles) && d.billing_cycles.length ? d.billing_cycles : ["monthly", "annual"],
      annual_duration: ["first", "periods", "forever"].includes(d.annual_duration) ? d.annual_duration : "forever",
      annual_periods: d.annual_periods || 1,
      max_redemptions: d.max_redemptions || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name || form.value <= 0) { toast.error("Name and value are required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        duration_months: form.duration === "repeating" ? (parseInt(form.duration_months) || 12) : null,
        annual_periods: form.annual_duration === "periods" ? (parseInt(form.annual_periods) || 1) : null,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null,
      };
      let res;
      if (editing) {
        res = await axios.put(`${API}/admin/discounts/${editing}`, payload, auth());
        toast.success("Discount updated");
      } else {
        res = await axios.post(`${API}/admin/discounts`, payload, auth());
        toast.success("Discount created");
      }
      if (res.data.stripe_error) {
        toast.warning(`Stripe sync issue: ${res.data.stripe_error}`, { duration: 6000 });
      }
      setDialogOpen(false);
      load();
    } catch { toast.error("Failed to save discount"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this discount?")) return;
    try {
      await axios.delete(`${API}/admin/discounts/${id}`, auth());
      toast.success("Discount deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const toggleActive = async (d) => {
    try {
      await axios.put(`${API}/admin/discounts/${d.discount_id}`, { is_active: !d.is_active }, auth());
      toast.success(d.is_active ? "Discount disabled" : "Discount enabled");
      load();
    } catch { toast.error("Failed to update"); }
  };

  const togglePlan = (planId) => {
    setForm(f => ({
      ...f,
      plan_ids: f.plan_ids.includes(planId) ? f.plan_ids.filter(p => p !== planId) : [...f.plan_ids, planId],
    }));
  };

  const isExpired = (d) => d.expires_at && new Date(d.expires_at) < new Date();
  const planName = (id) => plans.find(p => p.plan_id === id)?.name || id;

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope']">Discounts</h1>
          <p className="text-muted-foreground text-sm">Create and manage discounts for landing page pricing</p>
        </div>
        <Button onClick={openCreate} className="bg-[#FFCC00] text-black hover:bg-[#e6b800] font-semibold" data-testid="create-discount-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Discount
        </Button>
      </div>

      {discounts.length === 0 ? (
        <Card className="glass-card border-theme">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Tag className="w-10 h-10 text-muted-foreground mb-3" />
            <p className="text-foreground font-medium mb-1">No discounts yet</p>
            <p className="text-sm text-muted-foreground mb-4">Create your first discount to display on the landing page</p>
            <Button onClick={openCreate} variant="outline" className="border-theme">Create Discount</Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="glass-card border-theme">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-theme">
                  <TableHead className="text-muted-foreground">Name</TableHead>
                  <TableHead className="text-muted-foreground">Type</TableHead>
                  <TableHead className="text-muted-foreground">Value</TableHead>
                  <TableHead className="text-muted-foreground">Applies To</TableHead>
                  <TableHead className="text-muted-foreground">Cycles</TableHead>
                  <TableHead className="text-muted-foreground">Duration</TableHead>
                  <TableHead className="text-muted-foreground">Expires</TableHead>
                  <TableHead className="text-muted-foreground">Status</TableHead>
                  <TableHead className="text-muted-foreground">Stripe</TableHead>
                  <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {discounts.map(d => (
                  <TableRow key={d.discount_id} className="border-theme" data-testid={`discount-row-${d.discount_id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-foreground">{d.name}</p>
                        {d.marketing_text && <p className="text-xs text-muted-foreground mt-0.5">{d.marketing_text}</p>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-theme text-foreground">
                        {d.discount_type === "percentage" ? <Percent className="w-3 h-3 mr-1" /> : <DollarSign className="w-3 h-3 mr-1" />}
                        {d.discount_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-bold text-foreground">
                      {d.discount_type === "percentage" ? `${d.value}%` : `$${d.value}`}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {d.applies_to === "all" ? "All plans" : (d.plan_ids || []).map(planName).join(", ")}
                    </TableCell>
                    <TableCell data-testid={`discount-cycles-${d.discount_id}`}>
                      <Badge variant="outline" className="border-theme text-foreground text-xs">
                        {cycleKey(d.billing_cycles) === "both" ? "Monthly + Annual" : cycleKey(d.billing_cycles) === "annual" ? "Annual only" : "Monthly only"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground" data-testid={`discount-duration-${d.discount_id}`}>
                      <div>
                        {cycleKey(d.billing_cycles) !== "annual" && (
                          <p>
                            {d.duration === "repeating"
                              ? `Monthly: ${d.duration_months || 12} months`
                              : d.duration === "once"
                              ? "Monthly: First payment"
                              : d.expires_at && !d.duration
                              ? "Monthly: First payment"
                              : "Monthly: Forever"}
                          </p>
                        )}
                        {cycleKey(d.billing_cycles) !== "monthly" && (
                          <p>
                            {d.annual_duration === "periods"
                              ? `Annual: ${d.annual_periods || 1} renewal${(d.annual_periods || 1) > 1 ? "s" : ""}`
                              : d.annual_duration === "first"
                              ? "Annual: First payment"
                              : "Annual: Every renewal"}
                          </p>
                        )}
                        {d.max_redemptions ? (
                          <p className="text-[11px] text-muted-foreground/70">{d.usage_count || 0}/{d.max_redemptions} used</p>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      {d.expires_at ? (
                        <span className={`text-sm ${isExpired(d) ? "text-red-400" : "text-muted-foreground"}`}>
                          {isExpired(d) ? "Expired" : new Date(d.expires_at).toLocaleDateString()}
                        </span>
                      ) : <span className="text-sm text-muted-foreground">Never</span>}
                    </TableCell>
                    <TableCell>
                      <Switch checked={d.is_active && !isExpired(d)} onCheckedChange={() => toggleActive(d)} data-testid={`discount-toggle-${d.discount_id}`} />
                    </TableCell>
                    <TableCell>
                      {(d.stripe_coupon_id || d.stripe_coupon_id_annual) ? (
                        <Badge variant="outline" className="border-emerald-500/30 text-emerald-500 text-xs">Synced</Badge>
                      ) : (
                        <Badge variant="outline" className="border-amber-500/30 text-amber-500 text-xs">Not synced</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(d)} data-testid={`edit-discount-${d.discount_id}`}><Edit className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => remove(d.discount_id)} data-testid={`delete-discount-${d.discount_id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ─── Create / Edit Dialog ─── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background border-theme max-w-lg" data-testid="discount-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground font-['Manrope']">{editing ? "Edit Discount" : "Create Discount"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-muted-foreground text-sm">Discount Name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                className="bg-theme-surface border-theme text-foreground mt-1" placeholder="e.g., Launch Offer" data-testid="discount-name-input" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground text-sm">Type</Label>
                <Select value={form.discount_type} onValueChange={v => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger className="bg-theme-surface border-theme text-foreground mt-1" data-testid="discount-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-muted-foreground text-sm">Value</Label>
                <div className="relative mt-1">
                  {form.discount_type === "percentage" ? <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /> : <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />}
                  <Input type="number" value={form.value} onChange={e => setForm({ ...form, value: parseFloat(e.target.value) || 0 })}
                    className="bg-theme-surface border-theme text-foreground pl-9" data-testid="discount-value-input" />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-muted-foreground text-sm">Applies To</Label>
              <Select value={form.applies_to} onValueChange={v => setForm({ ...form, applies_to: v })}>
                <SelectTrigger className="bg-theme-surface border-theme text-foreground mt-1" data-testid="discount-applies-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  <SelectItem value="specific">Specific Plans</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.applies_to === "specific" && (
              <div>
                <Label className="text-muted-foreground text-sm mb-2 block">Select Plans</Label>
                <div className="flex flex-wrap gap-2">
                  {plans.filter(p => p.is_active).map(p => (
                    <button key={p.plan_id} type="button"
                      onClick={() => togglePlan(p.plan_id)}
                      className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors ${
                        form.plan_ids.includes(p.plan_id)
                          ? "bg-[#FFCC00]/20 border-[#FFCC00] text-[#FFCC00]"
                          : "bg-muted/50 border-theme text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`plan-chip-${p.plan_id}`}
                    >
                      {p.name} (${p.price}/mo)
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label className="text-muted-foreground text-sm">Billing Cycle Eligibility</Label>
              <p className="text-xs text-muted-foreground/60 mb-1">Which billing cycles this discount can apply to</p>
              <Select value={cycleKey(form.billing_cycles)} onValueChange={v => setForm({ ...form, billing_cycles: cyclesFromKey(v) })}>
                <SelectTrigger className="bg-theme-surface border-theme text-foreground" data-testid="discount-cycles-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Monthly & Annual</SelectItem>
                  <SelectItem value="monthly">Monthly subscriptions only</SelectItem>
                  <SelectItem value="annual">Annual subscriptions only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.billing_cycles.includes("monthly") && (
            <div>
              <Label className="text-muted-foreground text-sm">Monthly Discount Duration</Label>
              <p className="text-xs text-muted-foreground/60 mb-1">How long the discount applies to each subscriber's recurring monthly bill</p>
              <div className="grid grid-cols-2 gap-4">
                <Select value={form.duration} onValueChange={v => setForm({ ...form, duration: v })}>
                  <SelectTrigger className="bg-theme-surface border-theme text-foreground" data-testid="discount-duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="forever">Forever</SelectItem>
                    <SelectItem value="once">First payment only</SelectItem>
                    <SelectItem value="repeating">Limited months</SelectItem>
                  </SelectContent>
                </Select>
                {form.duration === "repeating" && (
                  <div className="relative">
                    <Input type="number" min="1" max="36" value={form.duration_months}
                      onChange={e => setForm({ ...form, duration_months: parseInt(e.target.value) || 1 })}
                      className="bg-theme-surface border-theme text-foreground pr-16" data-testid="discount-duration-months-input" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">months</span>
                  </div>
                )}
              </div>
            </div>
            )}

            {form.billing_cycles.includes("annual") && (
            <div>
              <Label className="text-muted-foreground text-sm">Annual Renewal Terms</Label>
              <p className="text-xs text-muted-foreground/60 mb-1">Applied to the annual invoice amount — first payment, a set number of annual renewals, or every renewal</p>
              <div className="grid grid-cols-2 gap-4">
                <Select value={form.annual_duration} onValueChange={v => setForm({ ...form, annual_duration: v })}>
                  <SelectTrigger className="bg-theme-surface border-theme text-foreground" data-testid="discount-annual-duration-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="forever">Every annual renewal</SelectItem>
                    <SelectItem value="first">First annual payment only</SelectItem>
                    <SelectItem value="periods">Limited annual periods</SelectItem>
                  </SelectContent>
                </Select>
                {form.annual_duration === "periods" && (
                  <div className="relative">
                    <Input type="number" min="1" max="10" value={form.annual_periods}
                      onChange={e => setForm({ ...form, annual_periods: parseInt(e.target.value) || 1 })}
                      className="bg-theme-surface border-theme text-foreground pr-14" data-testid="discount-annual-periods-input" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">years</span>
                  </div>
                )}
              </div>
            </div>
            )}

            <div>
              <Label className="text-muted-foreground text-sm">Usage Limit (optional)</Label>
              <p className="text-xs text-muted-foreground/60 mb-1">Max number of checkouts that can use this discount — blank for unlimited</p>
              <Input type="number" min="1" value={form.max_redemptions}
                onChange={e => setForm({ ...form, max_redemptions: e.target.value })}
                className="bg-theme-surface border-theme text-foreground" placeholder="Unlimited" data-testid="discount-max-redemptions-input" />
            </div>

            <div>
              <Label className="text-muted-foreground text-sm">Expiration Date (optional)</Label>
              <p className="text-xs text-muted-foreground/60 mb-1">When the OFFER stops being available to new signups (different from duration)</p>
              <Input type="datetime-local" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })}
                className="bg-theme-surface border-theme text-foreground mt-1" data-testid="discount-expiry-input" />
            </div>

            <div>
              <Label className="text-muted-foreground text-sm">Marketing Text (shown on landing page)</Label>
              <Input value={form.marketing_text} onChange={e => setForm({ ...form, marketing_text: e.target.value })}
                className="bg-theme-surface border-theme text-foreground mt-1" placeholder='e.g., "50% OFF for the first year"' data-testid="discount-marketing-input" />
            </div>

            <div>
              <Label className="text-muted-foreground text-sm">Badge Label (optional)</Label>
              <Input value={form.badge_label} onChange={e => setForm({ ...form, badge_label: e.target.value })}
                className="bg-theme-surface border-theme text-foreground mt-1" placeholder='e.g., "Limited Offer"' data-testid="discount-badge-input" />
            </div>

            <div className="flex items-center justify-between pt-1">
              <div>
                <Label className="text-muted-foreground text-sm">Apply to existing subscribers</Label>
                <p className="text-xs text-muted-foreground/60 mt-0.5">Push this discount to current active Stripe subscriptions. Otherwise, existing subscribers keep their current pricing — edits only affect new checkouts.</p>
              </div>
              <Switch checked={form.apply_to_existing} onCheckedChange={v => setForm({ ...form, apply_to_existing: v })} data-testid="discount-apply-existing-switch" />
            </div>

            <div className="flex items-center justify-between pt-1">
              <Label className="text-muted-foreground text-sm">Active</Label>
              <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} data-testid="discount-active-switch" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-theme">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#FFCC00] text-black hover:bg-[#e6b800] font-semibold" data-testid="discount-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDiscounts;
